import fp from "lodash/fp";
import { getFormValues } from "redux-form";

import { mapFeesForm } from "containers/application/applicationEntry/mapper";
import {
	ACHProcessorBoardingFields,
	APP_EDIT_FORM_NAME_FEES,
	ACHProcessorData as ach,
} from "globalConstants";
import {
	getStringWithoutHyphens,
	isDateValid,
	showFiservDataGridMessages,
} from "helpers";
import {
	mapSoftwareBillingInfo,
	mapSoftwareOptionsFileBuildOnly,
	mapSoftwareOptionsGeneralData,
} from "hooks";
import {
	additionalServicesMapper,
	deleteEmptyValues,
	mapAddressToApi,
	mapOwnerToApi,
	resetAchFields,
} from "mappers";
import { applicationEditService, dropdownValuesService } from "services";
import { businessInfoActions } from "store";
import { businessInfoConstants } from "store/applicationEntry/businessInfo/businessInfo.constants";

import { applicationEditConstants } from "./applicationEdit.constants";

export const applicationEditActions = {
	editApplication,
	getApplication,
	resetApplicationEditState,
};

function editApplication(
	originalApplication,
	isAppSubmitting = false,
	toFlow = false,
	isSigningSubmitting = false,
) {
	return (dispatch, getState) => {
		dispatch(request());
		const state = getState();

		const editedApplication = getFormValuesAndMapApplication(
			{ ...originalApplication, saveWithoutSendingPdf: !isAppSubmitting },
			state,
			isSigningSubmitting,
		);

		const saveOrSubmit = isAppSubmitting
			? !toFlow
				? applicationEditService.submitApplication
				: applicationEditService.finalizeApplication
			: applicationEditService.saveApplication;

		return saveOrSubmit(editedApplication).then(
			res => {
				showFiservDataGridMessages(
					res?.applied_fiserv_grids,
					res?.application_number,
					dispatch,
				);

				dispatch(success(res));
				return Promise.resolve();
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return {
			type: applicationEditConstants.EDIT_APPLICATION_SUBMIT_REQUEST,
		};
	}

	function success() {
		return {
			type: applicationEditConstants.EDIT_APPLICATION_SUBMIT_SUCCESS,
		};
	}

	function failure(error) {
		return {
			type: applicationEditConstants.EDIT_APPLICATION_SUBMIT_FAILURE,
			error,
		};
	}
}

function getFormValuesAndMapApplication(
	{ softwareOptionDefaults, ...originalApplication },
	state,
	isSigningSubmitting,
) {
	// prettier-ignore
	const processorFormValues =
		getFormValues("processorEditForm")(state) || {};
	// prettier-ignore
	const businessFormValues =
		getFormValues("businessEditForm")(state) || {};
	// prettier-ignore
	const feesEditFormValues =
		getFormValues(APP_EDIT_FORM_NAME_FEES)(state) || {};
	// prettier-ignore
	const principalFormValues =
		getFormValues("principalEditForm")(state) || {};
	// prettier-ignore
	const additionalServicesFormValues =
		getFormValues("additionalServicesEditForm")(state) || {};

	const softwareOptionFormValues = mapSoftwareOptions(
		softwareOptionDefaults || {},
	);

	const signApplicationEditForm =
		getFormValues("signApplicationEditForm")(state) || {};

	const editedApplication = {
		...originalApplication,
		...mapProcessorFormValues(processorFormValues, originalApplication),
		...mapBusinessInfo(
			businessFormValues,
			processorFormValues,
			originalApplication,
		),
		...mapOwnerPage(principalFormValues),
		...mapFeesForm(feesEditFormValues),
		...additionalServicesMapper(additionalServicesFormValues),
		...signApplicationEditForm,
		softwareOptionDefaults: softwareOptionFormValues,
		applicationId: originalApplication.id,
	};

	if (editedApplication.gatewayNameId === undefined) {
		editedApplication.gatewayNameId =
			editedApplication?.gatewayName !== null
				? editedApplication?.gatewayName?.id
				: null;
	}

	delete editedApplication.gatewayName;
	delete editedApplication.websiteInformation;

	const signerExists = editedApplication.signer?.id;

	editedApplication.authorizedSigner = signerExists
		? editedApplication.authorizedSigner
		: null;

	editedApplication.skipSigning = isSigningSubmitting
		? true
		: editedApplication.skipSigning;
	delete editedApplication.signer;

	deleteEmptyValues(editedApplication);

	return editedApplication;
}

function mapProcessorFormValues(processorFormValues, application) {
	const processorSponsorSection = processorFormValues.processorSponsorSection;
	const residualsSection = processorFormValues.residualsSection;
	return fp.isEmpty(processorFormValues)
		? {
				processorId: application?.processor?.id,
				primaryAgentId: application?.primaryAgent?.id,
				headquarterAccountId: application?.headquarters?.id,
				sponsorId: application?.sponsor?.id,
				processor: undefined,
				primaryAgent: undefined,
				sponsor: undefined,
				achProcessor: application?.achProcessor,
				scheduleAId: application?.scheduleAId,
				splitProfileId: application?.splitProfileId,
				mccCode: application?.mccCode,
				mccAliasId: application.mccAliasId,
				whatDoYouSell: application?.whatDoYouSell,
				detailedExplanationWhatDoYouSell:
					application?.detailedExplanationWhatDoYouSell,
		  }
		: {
				processorId: processorSponsorSection?.processor?.id,
				primaryAgentId: processorFormValues?.primaryAgentId,
				headquarterAccountId: processorFormValues?.headquarters?.id,
				sponsorId: processorSponsorSection?.sponsor?.id,
				processor: undefined,
				primaryAgent: undefined,
				sponsor: undefined,
				achProcessor: processorSponsorSection?.achProcessor,
				scheduleAId: residualsSection?.scheduleA?.id,
				splitProfileId: residualsSection?.splitProfile?.id,
				mccCode: processorFormValues?.mccCodeSection?.mccCode,
				mccAliasId: processorFormValues?.mccCodeSection?.mccAliasId,
				whatDoYouSell:
					processorFormValues?.mccCodeSection?.whatDoYouSell,
				detailedExplanationWhatDoYouSell:
					processorFormValues?.mccCodeSection
						?.detailedExplanationWhatDoYouSell,
		  };
}

function mapOwnerPage(principalForm) {
	if (fp.isEmpty(principalForm)) return principalForm;

	const signerExists = principalForm.ownerPrincipalSection?.signer?.id;

	const {
		ownerPrincipalSection,
		email,
		firstName,
		lastName,
		phone,
		titlePosition,
		...restPrincipal
	} = principalForm;

	const owner1 = mapOwnerToApi(ownerPrincipalSection?.owners[0]);
	const owner2 = ownerPrincipalSection?.owners[1]
		? mapOwnerToApi(ownerPrincipalSection?.owners[1])
		: null;
	const owner3 = ownerPrincipalSection?.owners[2]
		? mapOwnerToApi(ownerPrincipalSection?.owners[2])
		: null;
	const owner4 = ownerPrincipalSection?.owners[3]
		? mapOwnerToApi(ownerPrincipalSection?.owners[3])
		: null;
	const authorizedSigner = signerExists
		? mapOwnerToApi(ownerPrincipalSection?.signer)
		: null;

	const toSend = {
		...restPrincipal,
		...ownerPrincipalSection,
		owner1,
		owner2,
		owner3,
		owner4,
		authorizedSigner,
		primaryContact: {
			email,
			firstName,
			lastName,
			phone,
			titlePosition,
		},
		ownerPrincipalSection: undefined,
	};

	if (signerExists) {
		toSend.signer.ownership = toSend.signer.ownership || 0;
	}

	delete toSend.owners;
	return toSend;
}

const mapBusinessInfo = (values, processorValues, originalApplication) => {
	if (fp.isEmpty(values)) return values;
	const achProcessor = processorValues.processorSponsorSection?.achProcessor;

	const copyBillingInfo =
		values?.[ach.IsApplyBankAccountData.name] === "true";

	const {
		AchBoardingStatus,
		StraddleIdentifier,
		StraddleGateId,
		StraddleGateKeyId,
	} = ACHProcessorBoardingFields;

	const achProcessorData = {
		[ach.MonthlyVolumeAch.beName]: values?.[ach.MonthlyVolumeAch.name],
		[ach.MonthlyVolumeAchPayouts.beName]:
			values?.[ach.MonthlyVolumeAchPayouts.name],
		[ach.HighTicketAch.beName]: values?.[ach.HighTicketAch.name],
		[ach.HighTicketAchPayouts.beName]:
			values?.[ach.HighTicketAchPayouts.name],
		[ach.TicketCountAch.beName]: values?.[ach.TicketCountAch.name],
		[ach.TicketCountAchPayouts.beName]:
			values?.[ach.TicketCountAchPayouts.name],
		[ach.StatementDescriptor.beName]:
			values?.[ach.StatementDescriptor.name],
		[ach.UseOfPaymentServiceDescription.beName]:
			values?.[ach.UseOfPaymentServiceDescription.name],
		[ach.IsApplyBankAccountData.beName]:
			values?.[ach.IsApplyBankAccountData.name],
		[ach.BillingRouting.beName]: copyBillingInfo
			? values?.routing
			: values?.[ach.BillingRouting.name],
		[ach.BillingBank.beName]: copyBillingInfo
			? values?.bank
			: values?.[ach.BillingBank.name],
		[ach.BillingAccount.beName]: copyBillingInfo
			? values?.account
			: values?.[ach.BillingAccount.name],
		[ach.BillingAccountName.beName]: copyBillingInfo
			? values?.accountName
			: values?.[ach.BillingAccountName.name],
		[ach.CustomerType.beName]: values?.[ach.CustomerType.name],
		[ach.PaymentAuthorizationType.beName]:
			values?.[ach.PaymentAuthorizationType.name],
		[AchBoardingStatus.beName]:
			originalApplication?.achProcessorData?.[AchBoardingStatus.beName],
		[StraddleIdentifier.beName]:
			originalApplication?.achProcessorData?.[StraddleIdentifier.beName],
		[StraddleGateId.beName]:
			originalApplication?.achProcessorData?.[StraddleGateId.beName],
		[StraddleGateKeyId.beName]:
			originalApplication?.achProcessorData?.[StraddleGateKeyId.beName],
	};

	const businessAddress = mapAddressToApi(values?.businessAddress);
	const legalAddress = mapAddressToApi(values?.legalAddress);

	const isLegalAddressTheSame =
		values?.legalAddress?.isLegalAddressTheSame === "true" ||
		values?.legalAddress?.isLegalAddressTheSame === true;

	return {
		...values,
		taxId: getStringWithoutHyphens(values.taxId),
		swiped: values.swiped || "0.00",
		keyed: values.keyed || "0.00",
		eCommerce: values.eCommerce || "0.00",
		website: values.website?.toLowerCase(),
		excludeCardTypes: Object.entries(values?.excludeCardTypes || {})
			.filter(([, value]) => value)
			.map(([type]) => type),
		seasonalMonths: values?.seasonalMonths?.map(({ value }) => value),
		businessAddress,
		legalAddress: isLegalAddressTheSame ? null : legalAddress,
		isLegalAddressTheSame,
		businessEstablishedDate: isDateValid(
			"MM/DD/YYYY",
			values?.businessEstablishedDate,
		)
			? values?.businessEstablishedDate
			: null,
		achProcessorData: achProcessor ? achProcessorData : null,
		...resetAchFields,
	};
};

const mapSoftwareOptions = values => {
	if (fp.isEmpty(values)) return values;

	const generalData = mapSoftwareOptionsGeneralData(values);
	const fileBuildOnly = mapSoftwareOptionsFileBuildOnly(values);
	const billingInfo = mapSoftwareBillingInfo(values);
	return { ...generalData, ...fileBuildOnly, ...billingInfo };
};

function getApplication(id) {
	return dispatch => {
		dispatch(request());

		return applicationEditService.getApplication(id).then(
			async result => {
				if (result?.routing) {
					const body = {
						pattern: result?.routing,
						page: 0,
						pageSize: 20,
					};
					const banks = await dropdownValuesService.getBanks(body);
					dispatch(businessInfoActions.setBanks(banks?.items));
				}
				if (result?.achProcessorData?.billing_routing) {
					const body = {
						pattern: result?.achProcessorData?.billing_routing,
						page: 0,
						pageSize: 20,
					};
					const billingBanks = await dropdownValuesService.getBanks(
						body,
					);
					dispatch(
						businessInfoActions.setBillingBanks(
							billingBanks?.items,
						),
					);
				}

				dispatch(success(result));
				dispatch(successWebSite(result?.websiteInformation, false));

				return Promise.resolve(result);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return {
			type: applicationEditConstants.GET_APPLICATION_REQUEST,
		};
	}

	function success(application) {
		return {
			type: applicationEditConstants.GET_APPLICATION_SUCCESS,
			application,
		};
	}

	function failure(error) {
		return {
			type: applicationEditConstants.GET_APPLICATION_ERROR,
			error,
		};
	}

	function successWebSite(website, isValidated) {
		return {
			type: businessInfoConstants.WEB_SITE_CHECK_SUCCESS,
			website,
			isValidated,
		};
	}
}

function resetApplicationEditState() {
	return {
		type: applicationEditConstants.RESET_APPLICATION_EDIT_STATE,
	};
}
